"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  MessageCircle,
  Star,
  User,
} from "lucide-react";
import {
  ProfileStatInline,
  ProfileStatSeparator,
  ProfileRatingStatSkeleton,
  ProfileStatSkeleton,
} from "@/components/Profile/profile-stat-inline";
import AuthModal from "@/components/ui/auth-modal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UnifiedFollowButton } from "@/components/UnifiedFollowButton";
import { ChatDialog } from "@/components/chat/ChatDialog";
import { ChatTabSlider } from "@/components/chat/ChatTabSlider";
import { ProfileReviewScoreLink } from "@/components/reviews/profile-review-score-link";
import { shipFromCountryDisplay } from "@/lib/shipping/format-ship-from-display";
import { usePartyReviewStats } from "@/hooks/use-party-review-stats";
import { usePartyFollowersCount } from "@/hooks/use-party-followers-count";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets";
import { cn } from "@/lib/utils";

export type OrderPartyRole = "buyer" | "seller";
export type OrderPartyCardSurface = "plain" | "card";
export type OrderPartyCardTone = "default" | "dark";

export interface OrderPartyCardProps {
  role: OrderPartyRole;
  name: string;
  username?: string | null;
  avatarUrl?: string | null;
  userId?: string | null;
  currentUserId?: string | null;
  salesCount?: number | null;
  followersCount?: number | null;
  rating?: number | null;
  reviewCount?: number | null;
  initialFollowing?: boolean;
  onFollowChange?: (following: boolean) => void;
  surface?: OrderPartyCardSurface;
  messageVariant?: "primary" | "secondary";
  tone?: OrderPartyCardTone;
  showFollow?: boolean;
  showContact?: boolean;
  currentListing?: {
    id: string;
    title: string;
    price: string | number;
    image_urls?: string[];
    condition?: string;
    grade?: string;
    shipFromAddress?: { city: string | null; state: string | null; country: string | null } | null;
    scheduled_publish_at?: string | null;
  } | null;
  chatLayout?: "dialog" | "sheet";
}

function normalizeUsername(username?: string | null): string {
  if (typeof username !== "string") return "";
  return username.trim().replace(/^@+/, "");
}

function partyDisplayLine(opts: {
  username?: string | null;
  name?: string | null;
  unknown?: string;
}): string {
  const handle = normalizeUsername(opts.username);
  if (handle.length > 0) return `@${handle}`;
  const display = typeof opts.name === "string" ? opts.name.trim() : "";
  if (display.length === 0) return opts.unknown ?? "—";
  if (display.startsWith("@") || /\s/.test(display)) return display;
  return `@${display}`;
}

function PartyStatSegment({
  icon,
  label,
  secondary = false,
  tone,
}: {
  icon: ReactNode;
  label: string;
  secondary?: boolean;
  tone: OrderPartyCardTone;
}) {
  return (
    <div className="inline-flex min-w-0 max-w-full shrink items-center gap-1">
      <span
        className={cn(
          "shrink-0",
          secondary
            ? tone === "dark"
              ? "text-white/70"
              : "text-muted-foreground"
            : tone === "dark"
              ? "text-white"
              : "text-foreground",
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "truncate text-xs font-semibold",
          secondary
            ? tone === "dark"
              ? "text-white/70"
              : "text-muted-foreground"
            : tone === "dark"
              ? "text-white"
              : "text-foreground",
        )}
      >
        {label}
      </span>
    </div>
  );
}

function PartyCardShell({
  surface,
  tone,
  children,
}: {
  surface: OrderPartyCardSurface;
  tone: OrderPartyCardTone;
  children: ReactNode;
}) {
  if (surface === "plain") {
    return (
      <div
        className={cn(
          "flex flex-col gap-3 border-y py-4",
          tone === "dark" ? "border-white/10" : "border-border",
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl p-4",
        tone === "dark" ? "bg-white/5" : "bg-muted dark:bg-white/5",
      )}
    >
      {children}
    </div>
  );
}

export function OrderPartyCardSkeleton({
  surface = "plain",
  tone = "default",
}: {
  surface?: OrderPartyCardSurface;
  tone?: OrderPartyCardTone;
}) {
  return (
    <PartyCardShell surface={surface} tone={tone}>
      <div className="flex items-center gap-3">
        <Skeleton
          className={cn(
            "h-12 w-12 shrink-0 rounded-full",
            tone === "dark" && "bg-white/10",
          )}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton
            className={cn("h-4 w-28 rounded-full", tone === "dark" && "bg-white/10")}
          />
          <Skeleton
            className={cn("h-3 w-20 rounded-full", tone === "dark" && "bg-white/10")}
          />
        </div>
        <Skeleton
          className={cn("h-9 w-24 shrink-0 rounded-full", tone === "dark" && "bg-white/10")}
        />
      </div>
      <div className="flex items-center justify-center gap-2">
        <Skeleton
          className={cn("h-3 w-16 rounded-full", tone === "dark" && "bg-white/10")}
        />
        <Skeleton
          className={cn("h-3 w-16 rounded-full", tone === "dark" && "bg-white/10")}
        />
        <Skeleton
          className={cn("h-3 w-20 rounded-full", tone === "dark" && "bg-white/10")}
        />
      </div>
      <Skeleton
        className={cn("h-10 w-full rounded-full", tone === "dark" && "bg-white/10")}
      />
    </PartyCardShell>
  );
}

export function OrderPartyCard({
  role,
  name,
  username,
  avatarUrl,
  userId,
  currentUserId,
  salesCount,
  followersCount,
  rating: ratingProp,
  reviewCount: reviewCountProp,
  initialFollowing = false,
  onFollowChange = () => {},
  surface = "plain",
  messageVariant = "secondary",
  tone = "default",
  showFollow = true,
  showContact = true,
  currentListing = null,
  chatLayout = "sheet",
}: OrderPartyCardProps) {
  const tListing = useTranslations("listing");
  const tParty = useTranslations("listing.partyCard");
  const router = useRouter();
  const resolvedAvatarUrl = getAvatarUrl(avatarUrl ?? null);
  const [chatOpen, setChatOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isSelf = Boolean(currentUserId && userId && currentUserId === userId);
  const cleanUsername = normalizeUsername(username);
  const profileHref = cleanUsername ? `/${cleanUsername}` : null;

  const shouldFetchReviews =
    ratingProp === undefined && reviewCountProp === undefined && Boolean(userId);
  const fetchedReviewStats = usePartyReviewStats(userId, shouldFetchReviews);
  const shouldFetchFollowers = followersCount === undefined && Boolean(userId);
  const fetchedFollowersCount = usePartyFollowersCount(userId, shouldFetchFollowers);

  const resolvedRating =
    ratingProp !== undefined ? ratingProp : fetchedReviewStats.rating;
  const resolvedReviewCount =
    reviewCountProp !== undefined ? reviewCountProp : fetchedReviewStats.reviewCount;

  const hasNumericRating =
    typeof resolvedRating === "number" && Number.isFinite(resolvedRating);
  const hasReviewCount =
    typeof resolvedReviewCount === "number" && Number.isFinite(resolvedReviewCount);
  const hasNoReviews = hasReviewCount && resolvedReviewCount === 0;
  const hasPublishedReviews = hasReviewCount && resolvedReviewCount! > 0;

  const displaySalesCount =
    typeof salesCount === "number" && Number.isFinite(salesCount) ? salesCount : null;
  const displayFollowersCount =
    typeof followersCount === "number" && Number.isFinite(followersCount)
      ? followersCount
      : typeof fetchedFollowersCount === "number" && Number.isFinite(fetchedFollowersCount)
        ? fetchedFollowersCount
        : 0;

  const handleLine = partyDisplayLine({
    username,
    name,
    unknown:
      role === "seller" ? tParty("sellerFallback") : tParty("buyerFallback"),
  });

  const ratingLabel = hasNumericRating
    ? resolvedRating!.toFixed(1)
    : hasNoReviews
      ? tParty("ratingNoReviews")
      : tParty("ratingNew");

  const messageLabel =
    role === "seller" ? tListing("messageSeller") : tParty("messageBuyer");

  const messageButtonVariant =
    messageVariant === "secondary" || surface === "plain" ? "outline" : "default";

  const chatRoomName = useMemo(() => {
    if (!currentUserId || !userId) return "";
    return [currentUserId, userId].sort().join(":");
  }, [currentUserId, userId]);

  const chatInitialDraft = useMemo(() => {
    if (!currentListing || !userId) return null;
    return {
      listing: {
        id: currentListing.id,
        user_id: userId,
        title: currentListing.title,
        price:
          typeof currentListing.price === "string"
            ? Number(currentListing.price)
            : currentListing.price,
        image_urls: currentListing.image_urls || [],
        condition: currentListing.condition || "unknown",
        grade: currentListing.grade || "N/A",
        location: shipFromCountryDisplay(currentListing.shipFromAddress),
        scheduled_publish_at: currentListing.scheduled_publish_at ?? null,
        seller_username: cleanUsername || name,
        seller_avatar_url: avatarUrl ?? null,
      },
    };
  }, [avatarUrl, cleanUsername, currentListing, name, userId]);

  const prefetchProfile = useCallback(() => {
    if (profileHref) router.prefetch(profileHref);
  }, [profileHref, router]);

  const handleMessageClick = () => {
    if (!currentUserId) {
      setShowAuthModal(true);
      return;
    }
    if (isSelf) return;
    setChatOpen(true);
  };

  const canFollow = showFollow && Boolean(userId) && Boolean(currentUserId) && !isSelf;
  const canContact = showContact && Boolean(userId) && !isSelf;

  const identityBlock = (
    <>
      <div
        className={cn(
          "relative h-12 w-12 shrink-0 overflow-hidden rounded-full ring-[1.5px]",
          tone === "dark" ? "ring-white/20 bg-white/10" : "ring-border bg-muted",
        )}
      >
        {resolvedAvatarUrl ? (
          <Image
            // `h-12 w-12` = 48 CSS px -> SQUARE_SM. Sized at the render site:
            // the variable also feeds `AuthModal` (64 px) and
            // `UnifiedFollowButton`'s notification payload.
            src={imagePresetUrl(resolvedAvatarUrl, SQUARE_SM)}
            alt={name}
            width={48}
            height={48}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <User
              className={cn(
                "h-6 w-6",
                tone === "dark" ? "text-white/60" : "text-muted-foreground",
              )}
            />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-sm font-bold",
            tone === "dark" ? "text-white" : "text-foreground",
          )}
        >
          {name}
        </p>
        <p
          className={cn(
            "truncate text-xs",
            tone === "dark" ? "text-white/60" : "text-muted-foreground",
          )}
        >
          {handleLine}
        </p>
      </div>
    </>
  );

  return (
    <>
      <AuthModal
        isOpen={showAuthModal}
        onCloseAction={() => setShowAuthModal(false)}
        username={cleanUsername || name}
        userImage={resolvedAvatarUrl || undefined}
        message={tParty("authMessage")}
        title={tParty("authTitle")}
      />

      {currentUserId && !isSelf && chatOpen && chatRoomName ? (
        chatLayout === "dialog" ? (
          <ChatDialog
            open={chatOpen}
            onOpenChangeAction={setChatOpen}
            roomName={chatRoomName}
            username={currentUserId}
            initialDraft={chatInitialDraft}
          />
        ) : (
          <ChatTabSlider
            open={chatOpen}
            onOpenChangeAction={setChatOpen}
            currentUserId={currentUserId}
            mode="chat"
            roomName={chatRoomName}
            username={currentUserId}
            initialOtherProfile={{
              username: cleanUsername || name,
              avatar_url: avatarUrl ?? null,
            }}
            initialDraft={chatInitialDraft}
          />
        )
      ) : null}

      <PartyCardShell surface={surface} tone={tone}>
        <div className="flex items-center gap-3">
          {profileHref ? (
            <Link
              href={profileHref}
              prefetch
              onPointerEnter={prefetchProfile}
              onTouchStart={prefetchProfile}
              className="flex min-w-0 flex-1 items-center gap-3"
            >
              {identityBlock}
            </Link>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-3">{identityBlock}</div>
          )}

          {canFollow ? (
            <div className="max-w-[132px] shrink-0">
              <UnifiedFollowButton
                currentUserId={currentUserId ?? null}
                profileId={userId!}
                initialFollowing={initialFollowing}
                variant="profile-stats"
                context="product-header"
                profileUsername={cleanUsername || name}
                profileAvatarUrl={resolvedAvatarUrl}
                promptNotificationsOnFollow
                onFollowChange={onFollowChange}
                className={cn(
                  tone === "dark" &&
                    "border-white/30 bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black",
                )}
              />
            </div>
          ) : null}
        </div>

        {userId ? (
          <div
            className={cn(
              "flex flex-wrap items-center justify-center gap-1",
              tone === "dark" ? "text-white" : "text-foreground",
            )}
          >
            {shouldFetchReviews && fetchedReviewStats.isLoading ? (
              <>
                <ProfileRatingStatSkeleton />
                {displaySalesCount != null ? (
                  <>
                    <ProfileStatSeparator className={tone === "dark" ? "text-white/30" : undefined} />
                    <ProfileStatSkeleton />
                  </>
                ) : null}
                <ProfileStatSeparator className={tone === "dark" ? "text-white/30" : undefined} />
                <ProfileStatSkeleton />
              </>
            ) : (
              <>
                {hasPublishedReviews ? (
                  <ProfileReviewScoreLink
                    profileId={userId}
                    enabled
                    // We only reach this branch because OUR fetch already said
                    // the profile has reviews — hand the result down instead of
                    // letting the child fire the same request again. When the
                    // stats came in as props we never fetched, so there is
                    // nothing to hand down and the child resolves its own
                    // (it needs `profileReviewRole` for the dialog's tab).
                    stats={
                      shouldFetchReviews
                        ? {
                            rating: fetchedReviewStats.rating,
                            reviewCount: fetchedReviewStats.reviewCount,
                            profileReviewRole:
                              fetchedReviewStats.profileReviewRole,
                          }
                        : undefined
                    }
                    tone={tone}
                    showSkeletonWhileLoading
                  />
                ) : (
                  <PartyStatSegment
                    icon={
                      <Star
                        className={cn(
                          "h-3.5 w-3.5",
                          hasNumericRating && "fill-current",
                        )}
                      />
                    }
                    label={ratingLabel}
                    secondary={!hasNumericRating}
                    tone={tone}
                  />
                )}
                {displaySalesCount != null ? (
                  <>
                    <ProfileStatSeparator className={tone === "dark" ? "text-white/30" : undefined} />
                    <ProfileStatInline
                      stat="sales"
                      value={displaySalesCount}
                      iconClassName={tone === "dark" ? "text-white/70" : undefined}
                      valueClassName={tone === "dark" ? "text-white text-xs font-semibold" : "text-xs font-semibold"}
                    />
                  </>
                ) : null}
                <ProfileStatSeparator className={tone === "dark" ? "text-white/30" : undefined} />
                <ProfileStatInline
                  stat="followers"
                  value={displayFollowersCount}
                  loading={shouldFetchFollowers && fetchedFollowersCount === null}
                  iconClassName={tone === "dark" ? "text-white/70" : undefined}
                  valueClassName={tone === "dark" ? "text-white text-xs font-semibold" : "text-xs font-semibold"}
                />
              </>
            )}
          </div>
        ) : null}

        {canContact ? (
          <Button
            type="button"
            variant={messageButtonVariant}
            className={cn(
              "h-10 w-full rounded-full gap-2",
              tone === "dark" &&
                messageButtonVariant === "outline" &&
                "border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white",
              tone === "dark" &&
                messageButtonVariant === "default" &&
                "bg-white text-black hover:bg-white/90",
            )}
            onClick={handleMessageClick}
          >
            <MessageCircle className="h-4 w-4" />
            {messageLabel}
          </Button>
        ) : null}
      </PartyCardShell>
    </>
  );
}
